import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  Zap, 
  Shield, 
  DollarSign,
  Settings,
  Users,
  Video,
  Clock
} from 'lucide-react';

const LiveStreamFAQ = () => {
  const [openItem, setOpenItem] = useState(0);

  const faqCategories = [
    {
      title: "Getting Started",
      icon: Zap,
      color: "from-green-500 to-emerald-500",
      faqs: [
        {
          question: "How quickly can I set up my first live stream?",
          answer: "Most activity centers are streaming their first event within 10 minutes of signing up. Our guided setup wizard walks you through event creation, payment integration, and streaming configuration. You'll receive a unique streaming URL and family invitation tools immediately after setup."
        },
        {
          question: "Do I need special equipment or technical expertise?",
          answer: "Not at all! HiveShare Live works with any device that has a camera - your smartphone, tablet, laptop, or professional cameras. No technical expertise required. Our one-click streaming technology handles all the complex technical details automatically."
        },
        {
          question: "What internet speed do I need for streaming?",
          answer: "We recommend a minimum upload speed of 5 Mbps for HD streaming. Our adaptive streaming technology automatically adjusts quality based on your connection, ensuring a smooth experience even with fluctuating internet speeds."
        }
      ]
    },
    {
      title: "Pricing & Billing",
      icon: DollarSign,
      color: "from-blue-500 to-purple-500",
      faqs: [
        {
          question: "How does per-event pricing work?",
          answer: "You only pay when you stream an event - no monthly fees or hidden costs. Choose from our Basic ($29), Standard ($49), or Premium ($75) tiers based on your event size and needs. Payment is processed after your event ends successfully."
        },
        {
          question: "How do families pay to watch the stream?",
          answer: "Families pay directly through secure payment links you send them. You keep 95% of all revenue - we only take a 5% processing fee. Payments are deposited into your account within 2 business days of the event."
        },
        {
          question: "What if my event gets cancelled or has technical issues?",
          answer: "Full money-back guarantee! If your event gets cancelled or we experience technical issues that prevent streaming, you receive a complete refund. We also provide backup technical support during every live event."
        },
        {
          question: "Are there volume discounts for multiple events?",
          answer: "Yes! Save 10% on 5+ events per year, 15% on 10+ events, and 20% on 20+ events. Perfect for studios with multiple recitals, competitions, and showcases throughout the year."
        }
      ]
    },
    {
      title: "Family Experience",
      icon: Users,
      color: "from-purple-500 to-pink-500",
      faqs: [
        {
          question: "How do families access the live stream?",
          answer: "Families receive a secure, personalized link via email after purchasing access. No app downloads or account creation required - they simply click the link and start watching on any device with an internet connection."
        },
        {
          question: "Can multiple family members watch from different locations?",
          answer: "Absolutely! Each family's access link works on unlimited devices simultaneously. Grandparents in Florida, deployed parents overseas, and local relatives can all watch together. Perfect for military families and extended family members."
        },
        {
          question: "How long are recordings available to families?",
          answer: "Recording access varies by tier: 30 days (Basic), 60 days (Standard), or 90 days (Premium). Families can download HD recordings during this period for permanent keepsake storage. Extended access available for additional fee."
        },
        {
          question: "What happens if a family has technical issues during the stream?",
          answer: "We provide 24/7 family support during all live events. Families can contact our technical team via chat, email, or phone. We also offer backup viewing options and can troubleshoot connection issues in real-time."
        }
      ]
    },
    {
      title: "Technical Details",
      icon: Settings,
      color: "from-orange-500 to-red-500",
      faqs: [
        {
          question: "What video quality can I expect?",
          answer: "All streams broadcast in HD (1080p) with automatic quality adaptation. Premium tier offers 4K streaming capability. Our global CDN ensures smooth playback worldwide with less than 15-second latency from your camera to family screens."
        },
        {
          question: "Can I use multiple cameras during my event?",
          answer: "Yes! Standard and Premium tiers support multi-camera setups with live switching capabilities. Perfect for capturing different angles of dance routines, gymnastics equipment, or stage performances. Easy one-click switching between camera views."
        },
        {
          question: "How secure are the live streams?",
          answer: "Military-grade security with token-based family access, encrypted streaming, and private event URLs. Only families who purchase access can view your stream. We're COPPA compliant and never store family viewing data or personal information."
        },
        {
          question: "What if my internet connection drops during streaming?",
          answer: "Our automatic reconnection technology resumes streaming within 5-10 seconds of connection restoration. Families receive seamless viewing experience with minimal interruption. Recordings capture the complete event even during brief connection issues."
        }
      ]
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white mb-6 px-6 py-2 text-lg">
            <HelpCircle className="h-5 w-5 mr-2" />
            Frequently Asked Questions
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Everything You Need to
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Know</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            Get answers to the most common questions about HiveShare Live. Still have questions? Our support team is here to help 24/7.
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <div className="max-w-5xl mx-auto">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className="mb-12"
            >
              {/* Category Header */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`bg-gradient-to-r ${category.color} rounded-xl p-3`}>
                    <category.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{category.title}</h3>
                </div>
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {category.faqs.map((faq, faqIndex) => {
                  const globalIndex = categoryIndex * 10 + faqIndex; // Unique index across all categories
                  const isOpen = openItem === globalIndex;
                  
                  return (
                    <Card key={faqIndex} className="overflow-hidden hover:shadow-lg transition-all duration-300">
                      <CardContent className="p-0">
                        <button
                          onClick={() => setOpenItem(isOpen ? -1 : globalIndex)}
                          className="w-full text-left p-6 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                        >
                          <h4 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h4>
                          <div className="flex-shrink-0">
                            {isOpen ? (
                              <ChevronUp className="h-5 w-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                        </button>
                        
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-6 border-t border-gray-100">
                                <p className="text-gray-600 leading-relaxed pt-4">{faq.answer}</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white max-w-4xl mx-auto"
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Still Have Questions?
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            Our streaming experts are standing by to help you succeed with your first live event.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Clock className="h-8 w-8 text-white mb-3 mx-auto" />
              <h4 className="font-bold text-lg mb-2">24/7 Support</h4>
              <p className="text-blue-100 text-sm">Live chat, email, and phone support during all events</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Video className="h-8 w-8 text-white mb-3 mx-auto" />
              <h4 className="font-bold text-lg mb-2">Live Demo</h4>
              <p className="text-blue-100 text-sm">Personal walkthrough with streaming specialist</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Shield className="h-8 w-8 text-white mb-3 mx-auto" />
              <h4 className="font-bold text-lg mb-2">Success Guarantee</h4>
              <p className="text-blue-100 text-sm">We ensure your first event is a complete success</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-bold shadow-lg transition-all duration-300"
            >
              Chat with Expert Now
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-xl text-lg font-bold transition-all duration-300"
            >
              Schedule Phone Call
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamFAQ;